import React, {
  useState,
  useMemo,
  useLayoutEffect,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Button, Box } from "@chakra-ui/react";
import {
  ThemeProvider,
  ToggleBoldButton,
  ToggleItalicButton,
  ToggleUnderlineButton,
  Toolbar,
  CommandButtonGroup,
  CommandMenuItem,
  DecreaseFontSizeButton,
  DropdownButton,
  IncreaseFontSizeButton,
  HeadingLevelButtonGroup,
  ListButtonGroup,
  useCommands,
  useActive,
  useRemirrorContext,
  TableComponents,
  ToggleSubscriptButton,
  ToggleSuperscriptButton,
  CommandButton,
  FloatingToolbar,
  FloatingWrapper,
  useChainedCommands,
  useCurrentSelection,
  useExtensionEvent,
  useUpdateReason,
  useAttrs,
} from "@remirror/react";
import { createMarkPositioner, LinkExtension } from "remirror/extensions";
import "./EditorMenu.scss";

export const EditorMenu = () => {
  return (
    <ThemeProvider>
      <Toolbar>
        <ToggleBoldButton/>
        <ToggleItalicButton/>
        <ToggleUnderlineButton />
        <CommandButtonGroup>
          <DecreaseFontSizeButton />
          <FontSizeButtons />
          <IncreaseFontSizeButton />
        </CommandButtonGroup>
        <HeadingLevelButtonGroup showAll />
        <ListButtonGroup />
        <ToggleSubscriptButton />
        <ToggleSuperscriptButton />
        <CommandButtonGroup>
          <FontColorButtons />
          <HighlightButtons />
        </CommandButtonGroup>
        {/* <TableComponents />
        <CommandMenu /> */}
        <FloatingLinkToolbar />
      </Toolbar>
    </ThemeProvider>
  );
};

const FontSizeButtons = () => {
  const { setFontSize } = useCommands();
  const { fontSize } = useActive();
  const FONT_SIZES = ["8", "10", "12", "14", "16", "18", "24", "30"];
  return (
    <DropdownButton aria-label="Set font size" icon="fontSize">
      {FONT_SIZES.map((size) => (
        <CommandMenuItem
          key={size}
          commandName="setFontSize"
          onSelect={() => setFontSize(size)}
          enabled={setFontSize.enabled(size)}
          active={fontSize({ size })}
          label={size}
          icon={null}
          displayDescription={false}
        />
      ))}
    </DropdownButton>
  );
};

const FontColorButtons = () => {
  const { setTextColor } = useCommands();
  const { textColor } = useActive();
  const FONT_COLORS = ["red", "green", "blue", "white", "black", "purple", "lightcoral", "orchid"];
  
  return (
    <DropdownButton aria-label="Set font color" icon="fontColor">
      {FONT_COLORS.map((color) => (
        <CommandMenuItem
          key={color}
          commandName="setTextColor"
          onSelect={() => setTextColor(color)}
          enabled={setTextColor.enabled(color)}
          active={textColor({ color })}
          label={color}
          icon={null}
          displayDescription={false}
        />
      ))}
    </DropdownButton>
  );
};

const HighlightButtons = () => {
  const { setTextHighlight } = useCommands();
  const HIGHLIGHT_COLORS = ["white", "yellow", "orange", "red", "green", "blue", "purple"];
  
  return (
    <DropdownButton aria-label="Set highlight color" icon="pencilFill">
      {HIGHLIGHT_COLORS.map((color) => (
        <CommandMenuItem
          key={color}
          commandName="setTextHighlight"
          onSelect={() => setTextHighlight(color)}
          enabled={setTextHighlight.enabled(color)}
          label={color}
          icon={null}
          displayDescription={false}
        />
      ))}
    </DropdownButton>
  );
};

const CommandMenu = () => {
  const ctx = useRemirrorContext();
  const commands = ctx.commands;

  return (
    <>
      <Button
        onMouseDown={(event) => event.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          commands.createTable({
            rowsCount: 4,
            columnsCount: 4,
            withHeaderRow: true,
          });
        }}
        className="table"
      >
        Add Table
      </Button>
    </>
  );
};

function useLinkShortcut() {
  const [linkShortcut, setLinkShortcut] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useExtensionEvent(
    LinkExtension,
    "onShortcut",
    useCallback(
      (props) => {
        if (!isEditing) {
          setIsEditing(true);
        }

        return setLinkShortcut(props);
      },
      [isEditing]
    )
  );

  return { linkShortcut, isEditing, setIsEditing };
}

function useFloatingLinkState() {
  const chain = useChainedCommands();
  const { isEditing, linkShortcut, setIsEditing } = useLinkShortcut();
  const { to, empty } = useCurrentSelection();
  const url = useAttrs().link()?.href ?? "";
  const [href, setHref] = useState(url);

  // A positioner which only shows for links.
  const linkPositioner = useMemo(
    () => createMarkPositioner({ type: "link" }),
    []
  );

  const onRemove = useCallback(() => {
    return chain.removeLink().focus().run();
  }, [chain]);

  const updateReason = useUpdateReason();

  useLayoutEffect(() => {
    if (!isEditing) {
      return;
    }
    if (updateReason.doc || updateReason.selection) {
      setIsEditing(false);
    }
  }, [isEditing, setIsEditing, updateReason.doc, updateReason.selection]);

  useEffect(() => {
    setHref(url);
  }, [url]);

  const submitHref = useCallback(() => {
    setIsEditing(false);
    const range = linkShortcut ?? undefined;
    if (href === "") {
      chain.removeLink();
    } else {
      chain.updateLink({ href, auto: false }, range);
    }

    chain.focus(range?.to ?? to).run();
    setHref("");
  }, [setIsEditing, linkShortcut, chain, href, to]);

  const cancelHref = useCallback(() => {
    setIsEditing(false);
  }, [setIsEditing]);

  const clickEdit = useCallback(() => {
    if (empty) {
      chain.selectLink();
    }
    setIsEditing(true);
  }, [chain, empty, setIsEditing]);

  return useMemo(
    () => ({
      href,
      setHref,
      linkShortcut,
      linkPositioner,
      isEditing,
      clickEdit,
      onRemove,
      submitHref,
      cancelHref,
    }),
    [
      href,
      linkShortcut,
      linkPositioner,
      isEditing,
      clickEdit,
      onRemove,
      submitHref,
      cancelHref,
    ]
  );
}

const DelayAutoFocusInput = ({ autoFocus, ...rest }) => {
  const inputRef = useRef();

  useEffect(() => {
    if (!autoFocus) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [autoFocus]);

  return <input ref={inputRef} {...rest} />;
};

const FloatingLinkToolbar = () => {
  const {
    isEditing,
    linkPositioner,
    clickEdit,
    onRemove,
    submitHref,
    href,
    setHref,
    cancelHref,
  } = useFloatingLinkState();
  const active = useActive();
  const activeLink = active.link();
  const { empty } = useCurrentSelection();

  const handleClickEdit = useCallback(() => {
    clickEdit();
  }, [clickEdit]);

  const linkEditButtons = activeLink ? (
    <>
      <CommandButton
        commandName="updateLink"
        onSelect={handleClickEdit}
        icon="pencilLine"
        enabled
      />
      <CommandButton
        commandName="removeLink"
        onSelect={onRemove}
        icon="linkUnlink"
        enabled
      />
    </>
  ) : (
    <CommandButton
      commandName="updateLink"
      onSelect={handleClickEdit}
      icon="link"
      enabled
    />
  );

  return (
    <Box style={{ zIndex: "999" }}>
      {!isEditing && <FloatingToolbar>{linkEditButtons}</FloatingToolbar>}
      {!isEditing && empty && (
        <FloatingToolbar positioner={linkPositioner}>
          {linkEditButtons}
        </FloatingToolbar>
      )}

      <FloatingWrapper
        positioner="always"
        placement="bottom"
        enabled={isEditing}
        renderOutsideEditor
      >
        <DelayAutoFocusInput
          style={{
            width: "200px",
            background: "#fff",
            border: "1px solid grey",
            borderRadius: "5px",
            padding: "5px",
          }}
          autoFocus
          placeholder="Enter link..."
          onChange={(event) => setHref(event.target.value)}
          value={href}
          onKeyPress={(event) => {
            const { code } = event;

            if (code === "Enter") {
              submitHref();
            }

            if (code === "Escape") {
              cancelHref();
            }
          }}
        />
      </FloatingWrapper>
    </Box>
  );
};
